import { type DriveFile } from '@/shared/DriveTypes';
import { InfoOutlined } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';
import { useState } from 'react';
import useSWR from 'swr';
import { alignCenter, flex } from '~/app.css';
import { danger, primary } from '~/elements/Buttons/Button.css';
import { LEGACY } from '~/utils/constants';
import { useAuth, useWikis } from '~/utils/hooks/hooks';
import registerComponent from '~/utils/registerComponent';
import { cx, omit, rem } from '~/utils/utils';
import { Heading } from './Heading';

const headingText = 'Multiple wikis per domain';
const subTitleText = 'Assign multiple wikis to a single domain';

export const WikiDomainSelectorLoader = ({ wikiId }: { wikiId: string }) => {
  const { data: wikis, isLoading: wikisLoading } = useWikis();

  if (!wikis || wikisLoading) {
    return <CircularProgress />;
  }
  return <WikiDomainSelector wikis={wikis} wikiId={wikiId} />;
};

export type Domain = {
  id: string;
  url: string;
  ids?: string;
};

export const WikiDomainSelector = ({ wikis, wikiId }: { wikis: DriveFile[]; wikiId: string }) => {
  const [selectedWikiIds, setSelectedWikiIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectFocused, setSelectFocused] = useState(false);
  const { data: user } = useAuth();
  const { data: domain, mutate: mutateDomain } = useSWR<Domain | undefined>(
    `/domain?id=${wikiId}`,
    (url: string) => fetch(url).then((r) => r.json<Domain>()),
    LEGACY
      ? {
          revalidateIfStale: false,
          revalidateOnFocus: false,
          revalidateOnMount: true,
        }
      : undefined
  );
  const savedWikiIds = domain?.ids?.split(',') ?? [];
  const isSelectOpen = selectOpen || selectFocused || selectedWikiIds.length > 0 || savedWikiIds.length > 0;

  if (!user) return null;
  if (!domain) {
    return (
      <>
        <Heading headingText={headingText} subTitleText={subTitleText} />
        <Paper
          elevation={0}
          className="flex flex-ac"
          style={{
            marginTop: 14,
            border: '1px solid #9fb1fa',
            padding: '1em',
            background: '#d2dbff',
            color: '#22275a',
            lineHeight: 1,
          }}
        >
          <InfoOutlined style={{ marginRight: 12, color: '#1c226cff' }} />
          Requires custom domain to be set
        </Paper>
      </>
    );
  }

  return (
    <div>
      <Heading headingText={headingText} subTitleText={subTitleText} />
      <FormControl style={{ width: '100%', marginTop: -4, marginBottom: '1rem' }}>
        <InputLabel
          style={{
            fontFamily: 'var(--font-title)',
            marginLeft: isSelectOpen ? -12 : 0,
            marginTop: isSelectOpen ? 0 : 10,
          }}
          htmlFor="select-multiple"
        >
          Select wikis
        </InputLabel>
        <Select
          disabled={!domain || loading || savedWikiIds.length > 0}
          title={wikis
            .filter((o) => savedWikiIds.includes(o.id))
            .map((o) => o.name)
            .join(', ')}
          multiple
          value={savedWikiIds.length > 0 ? savedWikiIds : selectedWikiIds.length > 0 ? selectedWikiIds : savedWikiIds}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={() => setSelectOpen(false)}
          onFocus={() => setSelectFocused(true)}
          onBlur={() => setSelectFocused(false)}
          onChange={(e) => {
            const value = e.target.value as string[];
            const sortedIds = wikis.filter((o) => value.includes(o.id)).map((o) => o.id);
            setSelectedWikiIds(sortedIds);
          }}
          input={<Input id="select-multiple" style={{ background: '#fff', padding: '.5rem' }} />}
          renderValue={(selected) => selected.map((id) => wikis.find((wiki) => wiki.id === id)?.name).join(', ')}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 224,
                width: 250,
              },
            },
          }}
        >
          {wikis.map((wiki) => (
            <MenuItem key={wiki.id} value={wiki.id}>
              <Checkbox checked={selectedWikiIds.includes(wiki.id)} />
              <ListItemText primary={wiki.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <div className={cx(flex, alignCenter)} style={{ marginTop: rem(10), gap: rem(16) }}>
        {savedWikiIds.length === 0 && (
          <div className={cx(flex, alignCenter)}>
            <button
              className={primary}
              disabled={loading || selectedWikiIds.length === 0}
              onClick={async (e) => {
                e.preventDefault();
                setLoading(true);
                await fetch('/api/domain', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    user: {
                      id: user.id,
                      idToken: user.tokens.id_token,
                      accessToken: user.tokens.access_token,
                    },
                    data: {
                      id: wikiId,
                      ids: selectedWikiIds.join(','),
                      url: domain.url,
                    },
                  }),
                })
                  .then((_) => {
                    void mutateDomain({
                      id: wikiId,
                      ids: selectedWikiIds.join(','),
                      url: domain.url ?? '',
                    });
                  })
                  .finally(() => {
                    setSelectedWikiIds([]);
                    setLoading(false);
                  });
              }}
              type="button"
            >
              <span>Save</span>
            </button>
            {loading && (
              <>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <CircularProgress size={22} />
              </>
            )}
          </div>
        )}

        {savedWikiIds.length > 0 && (
          <div className={cx(flex, alignCenter)}>
            <button
              className={danger}
              onClick={async (e) => {
                e.preventDefault();
                if (domain) {
                  setLoading(true);
                  await fetch('/api/domain', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      user: {
                        id: user.id,
                        idToken: user.tokens.id_token,
                        accessToken: user.tokens.access_token,
                      },
                      data: {
                        id: wikiId,
                        url: domain.url,
                      },
                    }),
                  })
                    .then(() => {
                      void mutateDomain(omit(domain, 'ids') as Domain, false);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }
              }}
              type="button"
            >
              <span>Remove</span>
            </button>
            {loading && (
              <>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <CircularProgress size={22} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

if (LEGACY) {
  registerComponent(WikiDomainSelector, 'wc-wiki-domain-selector', {
    wikiId: 'string',
    wikis: 'json',
  });
}
